/**
 * 运营工作台
 * 订单只有 PAID/REFUNDED；分账和开票在订单确认收货后自动完成。
 * 运营主要做：商户审核、监控订单/资金/发票、对账。
 */

import { Card, useApi, money, Badge, Loading, Button } from '../../components/ui'
import { HelpBubble } from '../../components/HelpBubble'
import { api } from '../../api/client'

interface Merchant {
  merchant_id: string
  company_name: string
  type: string
  status: string
}
interface Order {
  order_id: string
  amount_total: string
  amount_refunded: string
  status: string
}
interface Settlement {
  settlement_id: string
  order_id: string
  status: string
  platform_fee: string
}

export function AdminDashboard() {
  const merchants = useApi<Merchant[]>(async () => api.get('/admin/merchants?status=PENDING'), [])
  const orders = useApi<Order[]>(async () => api.get('/orders'), [])
  const settlements = useApi<Settlement[]>(async () => api.get('/admin/settlements'), [])

  const paidOrders = (orders.data ?? []).filter((o) => o.status === 'PAID')
  const refundedOrders = (orders.data ?? []).filter((o) => o.status === 'REFUNDED')
  const totalGmv = (orders.data ?? []).reduce((s, o) => s + parseFloat(o.amount_total), 0)
  const totalFee = (settlements.data ?? []).filter((s) => s.status === 'SUCCESS').reduce((s, x) => s + parseFloat(x.platform_fee), 0)

  if (settlements.loading) return <Loading />

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>运营工作台</h2>

      {/* 指标 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 16 }}>
        <Metric label="待审核商户" value={merchants.data?.length ?? 0} highlight={!!merchants.data?.length} />
        <Metric label="已支付订单" value={paidOrders.length} />
        <Metric label="已退款订单" value={refundedOrders.length} />
        <Metric label="累计 GMV" value={money(totalGmv)} />
      </div>

      {/* 待办：待审核商户 */}
      {merchants.data && merchants.data.length > 0 && (
        <Card title={`🔔 待审核商户（${merchants.data.length}）`} action={<Button size="sm" onClick={() => location.hash = '#/admin/merchants'}>去审核</Button>}>
          <div style={{ fontSize: 13 }}>
            有 {merchants.data.length} 个商户提交了入驻申请，等待审核通过后自动开立银行子户。
          </div>
        </Card>
      )}

      {/* 订单同步说明 */}
      <Card title="📦 订单同步（INT-A）">
        <div style={{ fontSize: 13, color: '#666' }}>
          订单从电商平台同步过来，只有 PAID（支付成功）和 REFUNDED（退款成功）两种状态。
          分账和开票在订单确认收货后<strong>自动完成</strong>，无需手动触发。
          退款同步来时，已开票的订单会自动反向分账+红冲。
        </div>
      </Card>

      {/* 平台收益 */}
      <Card title="💰 平台收益">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          <Metric label="累计抽佣" value={money(totalFee)} />
          <Metric label="成功分账笔数" value={settlements.data?.filter((s) => s.status === 'SUCCESS').length ?? 0} />
          <Metric label="已反向分账" value={settlements.data?.filter((s) => s.status === 'REVERSED').length ?? 0} />
        </div>
      </Card>

      <HelpBubble pageKey="admin-dashboard" title="运营工作台">
        <p>订单只有<b>已支付</b>和<b>已退款</b>两种状态。分账和开票在订单确认收货后<b>自动完成</b>。</p>
        <p>运营主要做：商户审核、监控订单/资金/发票、对账。退款同步来时已开票的自动反向分账+红冲。</p>
      </HelpBubble>
    </div>
  )
}

function Metric({ label, value, highlight }: { label: string; value: string | number; highlight?: boolean }) {
  return (
    <div style={{ background: '#fff', padding: 12, borderRadius: 6, border: highlight ? '2px solid #f59e0b' : '1px solid #e5e7eb' }}>
      <div style={{ color: '#888', fontSize: 11 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4, color: highlight ? '#d97706' : undefined }}>{value}</div>
    </div>
  )
}
